import { useLayoutEffect, useRef, useState } from 'react';

import { motion } from 'motion/react';

import { Heading3 } from '../Text';

import * as css from './Tabs.css';

export type TabItem = {
    /** Unique identifier for the tab. */
    id: string;

    /** Display label for the tab; falls back to `id` if not provided. */
    label?: string;

    /** Optional numeric badge displayed next to the label when greater than 0. */
    badge?: number;

    /** Whether the tab is disabled (non-interactive). */
    disabled?: boolean;
};

interface TabsProps {
    /** List of tab items to render. */
    items: TabItem[];

    /** The currently active tab id (controlled). */
    activeId: string;

    /** Callback fired when a tab is clicked, returning the selected tab id. */
    onClick: (id: TabItem['id']) => void;
}

export function Tabs({ items, activeId, onClick }: TabsProps) {
    const tabRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const [underlineStyle, setUnderlineStyle] = useState<{ left: number; width: number }>({ left: 0, width: 0 });

    useLayoutEffect(() => {
        const activeTab = tabRefs.current[activeId];
        if (activeTab) {
            const { offsetLeft, offsetWidth } = activeTab;
            setUnderlineStyle({ left: offsetLeft, width: offsetWidth });
        }
    }, [activeId]);

    return (
        <div className={css.tabsWrapper}>
            <div className={css.tabs}>
                {items.map((item) => (
                    <div
                        key={item.id}
                        className={css.item({ active: item.id === activeId })}
                        role="button"
                        onClick={() => onClick(item.id)}
                        ref={(el) => {
                            tabRefs.current[item.id] = el;
                        }}
                    >
                        <Heading3 className={css.label} color="primary">
                            {item.label ?? item.id}
                            {Number(item.badge) > 0 && <span className={css.badge}>{item.badge}</span>}
                        </Heading3>
                    </div>
                ))}
                <div className={css.track} />
                <motion.div
                    className={css.underline}
                    animate={{ left: underlineStyle.left, width: underlineStyle.width }}
                    initial={false}
                    transition={{ duration: 0.2, ease: 'circOut' }}
                />
            </div>
        </div>
    );
}
